import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlayCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { AutomationBookingLink } from "./AutomationBookingLink";
import { useBookingEffReferences } from "./useBookingEffReferences";
import { supabase } from "../../lib/supabase";
import { LEANCUSTOMS_UTB_TTW_AUTOMATION_ID } from "../../config/bookingAutomations";
import {
  AUTOMATION_OUTCOME_COLORS,
  AUTOMATION_OUTCOME_LABELS,
} from "./automationOutcomeLabels";
import { DocumentCheckTag } from "./DocumentCheckTag";

const { Text, Paragraph } = Typography;

interface UpdatedBookingRow {
  booking_id: number;
  declaration_id: string;
  previous_status: string | null;
  new_status: string | null;
  outcome: string;
  has_utb?: boolean | null;
  has_ttw?: boolean | null;
  document_count?: number | null;
}

interface SyncResponse {
  success?: boolean;
  dry_run?: boolean;
  error?: string;
  reason?: string;
  results?: UpdatedBookingRow[];
}

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  missing_authorization: "No auth token sent — refresh the page and try again.",
  invalid_session: "Session expired or invalid — sign out and back in.",
  not_admin: "Your account is not in admin_users.",
  server_misconfigured: "Function is missing server configuration.",
};

const UPDATED_OUTCOMES = ["updated", "would_update"];

export const AutomationCustomsClearedPreview: React.FC = () => {
  const [updatedBookings, setUpdatedBookings] = useState<UpdatedBookingRow[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [lastCheckedAt, setLastCheckedAt] = useState<string | null>(null);

  const bookingIds = useMemo(
    () => updatedBookings.map((b) => b.booking_id),
    [updatedBookings]
  );
  const bookingRefs = useBookingEffReferences(bookingIds);

  const loadLatestUpdated = useCallback(async () => {
    const { data: runs, error: runsError } = await supabase
      .from("booking_automation_runs")
      .select("id, started_at")
      .eq("automation_id", LEANCUSTOMS_UTB_TTW_AUTOMATION_ID)
      .order("started_at", { ascending: false })
      .limit(1);

    if (runsError || !runs?.length) {
      setUpdatedBookings([]);
      return;
    }

    const run = runs[0];
    setLastCheckedAt(run.started_at);

    const { data: items, error: itemsError } = await supabase
      .from("booking_automation_run_items")
      .select(
        "booking_id, declaration_id, previous_status, new_status, outcome, has_utb, has_ttw, document_count"
      )
      .eq("run_id", run.id)
      .in("outcome", UPDATED_OUTCOMES)
      .order("booking_id", { ascending: true });

    if (itemsError) {
      setUpdatedBookings([]);
      return;
    }

    setUpdatedBookings((items as UpdatedBookingRow[]) ?? []);
  }, []);

  useEffect(() => {
    (async () => {
      setInitialLoading(true);
      await loadLatestUpdated();
      setInitialLoading(false);
    })();
  }, [loadLatestUpdated]);

  const runCheck = async () => {
    setLoading(true);
    try {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (sessionError || !token) {
        message.error("Not logged in — refresh the page and try again.");
        return;
      }

      const { data, error } = await supabase.functions.invoke(
        "leancustoms-customs-cleared-sync",
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (error) {
        const body = data as SyncResponse | null;
        const reason = body?.reason
          ? AUTH_ERROR_MESSAGES[body.reason] ?? body.reason
          : null;
        message.error(reason || error.message || "Run failed");
        return;
      }

      const body = data as SyncResponse;
      if (!body?.success) {
        message.error(body?.error || "Run failed");
        return;
      }

      const updated =
        body.results?.filter((r) => UPDATED_OUTCOMES.includes(r.outcome)) ?? [];

      setUpdatedBookings(updated);
      setLastCheckedAt(new Date().toISOString());

      if (body.dry_run) {
        message.warning(
          updated.length > 0
            ? `Dry run only: ${updated.length} booking(s) would be updated (set LEANCUSTOMS_CUSTOMS_CLEARED_DRY_RUN=false on the function).`
            : "Dry run complete: no bookings to update."
        );
      } else {
        message.success(
          updated.length > 0
            ? `${updated.length} booking(s) updated to customs_cleared.`
            : "Run complete: no bookings met all conditions."
        );
      }

      await loadLatestUpdated();
    } catch (err) {
      message.error(err instanceof Error ? err.message : "Run failed");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Booking",
      dataIndex: "booking_id",
      key: "booking_id",
      width: 130,
      render: (id: number) => (
        <AutomationBookingLink bookingId={id} refs={bookingRefs} />
      ),
    },
    {
      title: "Previous status",
      dataIndex: "previous_status",
      key: "previous_status",
      render: (s: string | null) => <Text code>{s ?? "—"}</Text>,
    },
    {
      title: "Set to",
      dataIndex: "new_status",
      key: "new_status",
      render: (s: string | null) => (
        <Tag color="green">{s ?? "customs_cleared"}</Tag>
      ),
    },
    {
      title: "UTB",
      dataIndex: "has_utb",
      key: "has_utb",
      width: 60,
      render: (v: boolean | null | undefined) => (
        <DocumentCheckTag present={v} />
      ),
    },
    {
      title: "TTW",
      dataIndex: "has_ttw",
      key: "has_ttw",
      width: 60,
      render: (v: boolean | null | undefined) => (
        <DocumentCheckTag present={v} />
      ),
    },
    {
      title: "Docs",
      dataIndex: "document_count",
      key: "document_count",
      width: 55,
      render: (n: number | null | undefined) => (n != null ? n : "—"),
    },
    {
      title: "Declaration",
      dataIndex: "declaration_id",
      key: "declaration_id",
      render: (id: string) => (
        <Text copyable={{ text: id }} style={{ fontSize: 11 }}>
          {id.slice(0, 8)}…
        </Text>
      ),
    },
    {
      title: "Outcome",
      dataIndex: "outcome",
      key: "outcome",
      render: (outcome: string) => (
        <Tag color={AUTOMATION_OUTCOME_COLORS[outcome] ?? "default"}>
          {AUTOMATION_OUTCOME_LABELS[outcome] ?? outcome}
        </Tag>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert
        type="info"
        showIcon
        message="Run check updates status automatically"
        description="Checks eligible bookings for UTB + TTW on the linked LeanCustoms declaration and sets customs_cleared when all conditions are met. Status-change emails fire as usual."
      />

      <Space wrap>
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          onClick={runCheck}
          loading={loading}
        >
          Run check now
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={loadLatestUpdated}
          disabled={loading}
        >
          Refresh list
        </Button>
        {lastCheckedAt && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Last run: {new Date(lastCheckedAt).toLocaleString()}
          </Text>
        )}
      </Space>

      <div>
        <Text strong>
          Updated in last run ({updatedBookings.length})
        </Text>
        <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 4 }}>
          Bookings that were set to customs_cleared in the most recent run (manual
          or cron). Expand run history below for skipped bookings.
        </Paragraph>

        {initialLoading ? (
          <Spin />
        ) : (
          <Table
            rowKey="booking_id"
            size="small"
            columns={columns}
            dataSource={updatedBookings}
            pagination={false}
            locale={{
              emptyText:
                "No bookings updated in the last run — run a check or wait for cron.",
            }}
          />
        )}
      </div>
    </Space>
  );
};
